import { alert } from '@/services/alert';
import { useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';
import { trackDirectLinkCopied } from '@/services/analytics';

interface UseCopyDirectLinkOptions {
  paramName: string;
  errorTitle?: string;
  errorMessage?: string;
  // Optional analytics tracking
  trackingEnabled?: boolean;
  getItemType?: (itemReference: string) => 'call' | 'sms' | 'chat' | 'note' | 'fax' | 'unknown';
  patientId?: string;
}

export function useCopyDirectLink(options: UseCopyDirectLinkOptions) {
  const {
    paramName,
    errorTitle = "Couldn't copy link",
    errorMessage = "We couldn't copy the direct link to your clipboard.",
    trackingEnabled = false,
    getItemType,
    patientId,
  } = options;

  const posthog = usePostHog();

  const copyDirectLink = useCallback(
    async (itemReference: string) => {
      try {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set(paramName, itemReference);
        await navigator.clipboard.writeText(currentUrl.toString());

        // Track direct link copied
        if (trackingEnabled && getItemType && patientId) {
          trackDirectLinkCopied(posthog, {
            item_type: getItemType(itemReference),
            source: 'kebab menu',
            patient_id: patientId,
          });
        }
      } catch {
        alert.error({ title: errorTitle, message: errorMessage });
      }
    },
    [paramName, errorTitle, errorMessage, trackingEnabled, getItemType, patientId, posthog]
  );

  return { copyDirectLink };
}
